import React, { useState, useMemo } from "react";
import { v4 as uuidV4 } from "uuid";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { type CfrSuggestion, type ListCfrTemplateItem } from "@/lib/api/cfr";
import { CommodityOptions } from "@/lib/api/commodity";
import { getUniqueStrings } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { useBulkSendBatchSubmitter, type BulkSendInstancePayload } from "./useBulkSendBatchSubmitter";
import { BulkSendBatchProgressView } from "./BulkSendBatchProgressView";

interface BulkCommodityMarkSendDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    selectedSuggestions: CfrSuggestion[];
    templateData: ListCfrTemplateItem[];
}

export function BulkCommodityMarkSendDialog({
    open,
    onOpenChange,
    selectedSuggestions,
    templateData,
}: BulkCommodityMarkSendDialogProps) {
    const { toast } = useToast();
    const { phase, isSubmitting, submit, close } = useBulkSendBatchSubmitter({
        onBeforeRedirect: () => onOpenChange(false),
    });

    const [isTestSend, setIsTestSend] = useState(false);

    // Get unique commodities from selected suggestions (read-only)
    const uniqueCommodities = useMemo(() => {
        const commodityValues = getUniqueStrings(
            selectedSuggestions
                .map(s => s.commodity)
                .filter((c): c is string => !!c)
        );
        return commodityValues.map(commodity => {
            const option = CommodityOptions.find(opt => opt.value === commodity);
            return {
                value: commodity,
                label: option?.label || commodity,
            };
        }).sort((a, b) => a.label.localeCompare(b.label));
    }, [selectedSuggestions]);

    // Get unique crop years from selected suggestions (read-only)
    const uniqueCropYears = useMemo(() => {
        const cropYears = selectedSuggestions
            .map(s => s.crop_year)
            .filter((year): year is number => typeof year === 'number');
        return Array.from(new Set(cropYears)).sort((a, b) => b - a);
    }, [selectedSuggestions]);

    // Template matching logic - many-to-many matching
    const matchingResults = useMemo(() => {
        const matchedContracts: Array<{
            suggestion: CfrSuggestion;
            template: ListCfrTemplateItem;
        }> = [];
        const unmatchedSuggestions: CfrSuggestion[] = [];

        for (const suggestion of selectedSuggestions) {
            // Skip suggestions without required fields
            if (!suggestion.commodity || !suggestion.crop_year || !suggestion.customer_id) {
                unmatchedSuggestions.push(suggestion);
                continue;
            }

            // Find ALL templates matching crop year and commodity
            const matchingTemplates = templateData.filter(template =>
                template.crop_year === suggestion.crop_year &&
                template.commodity_type === suggestion.commodity
            );

            if (matchingTemplates.length === 0) {
                unmatchedSuggestions.push(suggestion);
            } else {
                // Create a match entry for each template (many-to-many)
                for (const template of matchingTemplates) {
                    matchedContracts.push({
                        suggestion,
                        template,
                    });
                }
            }
        }

        return {
            matchedContracts,
            unmatchedSuggestions,
        };
    }, [selectedSuggestions, templateData]);

    const handleClose = () => {
        if (isSubmitting) { return; }
        close();
        onOpenChange(false);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (isSubmitting) return;

        if (selectedSuggestions.length === 0) {
            toast({ title: "Error", description: "No suggestions selected", variant: "destructive" });
            return;
        }
        if (matchingResults.matchedContracts.length === 0) {
            toast({
                title: "Error",
                description: "No valid template matches found. All suggestions are unmatched.",
                variant: "destructive",
            });
            return;
        }

        const payloads: BulkSendInstancePayload[] = matchingResults.matchedContracts.map(({ suggestion, template }) => ({
            cfr_document_id: template.id,
            group_id: uuidV4(),
            template_id: template.DocumentTemplate!.id,
            customer_id: suggestion.customer_id!,
            is_test_send: isTestSend,
            is_draft: false,
            suggestion_id: suggestion.id,
        }));

        await submit(payloads);
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) { handleClose(); } else { onOpenChange(o); } }}>
            <DialogContent className="sm:max-w-[550px]">
                <DialogHeader>
                    <DialogTitle>Bulk Send CFR Templates (Commodity Mark)</DialogTitle>
                </DialogHeader>

                {phase.kind !== "idle" ? (
                    <BulkSendBatchProgressView phase={phase} onClose={handleClose} />
                ) : (
                    <form onSubmit={handleSubmit}>
                        <div className="grid gap-4 py-4">
                            <div className="grid gap-2">
                                <Label>Selected Suggestions</Label>
                                <div className="text-sm text-muted-foreground">
                                    {selectedSuggestions.length} suggestion(s) selected
                                </div>
                            </div>

                            <div className="grid gap-2">
                                <Label htmlFor="is_test_send">Is Test Send</Label>
                                <Checkbox
                                    id="is_test_send"
                                    checked={isTestSend}
                                    onCheckedChange={(checked) => setIsTestSend(!!checked)}
                                />
                            </div>

                            <div className="grid gap-2">
                                <Label>Commodities (from selected suggestions)</Label>
                                <div className="flex flex-wrap gap-2">
                                    {uniqueCommodities.length > 0 ? (
                                        uniqueCommodities.map((commodity) => (
                                            <Badge key={commodity.value} variant="secondary">
                                                {commodity.label}
                                            </Badge>
                                        ))
                                    ) : (
                                        <span className="text-sm text-muted-foreground">No commodities found</span>
                                    )}
                                </div>
                            </div>

                            <div className="grid gap-2">
                                <Label>Crop Years (from selected suggestions)</Label>
                                <div className="flex flex-wrap gap-2">
                                    {uniqueCropYears.length > 0 ? (
                                        uniqueCropYears.map((year) => (
                                            <Badge key={year} variant="secondary">
                                                {year}
                                            </Badge>
                                        ))
                                    ) : (
                                        <span className="text-sm text-muted-foreground">No crop years found</span>
                                    )}
                                </div>
                            </div>

                            <div className="grid gap-2">
                                <Label>Template Matching Results</Label>
                                <div className="space-y-2 text-sm">
                                    <div className="flex items-center gap-2">
                                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                                        <span className="font-medium text-green-600">
                                            {matchingResults.matchedContracts.length} contract(s) to send
                                        </span>
                                        <span className="text-muted-foreground">
                                            - {selectedSuggestions.length - matchingResults.unmatchedSuggestions.length} matched suggestion(s)
                                        </span>
                                    </div>
                                    {matchingResults.unmatchedSuggestions.length > 0 && (
                                        <div className="flex items-center gap-2">
                                            <AlertCircle className="h-4 w-4 text-yellow-600" />
                                            <span className="font-medium text-yellow-600">
                                                {matchingResults.unmatchedSuggestions.length} unmatched suggestion(s)
                                            </span>
                                            <span className="text-muted-foreground">- No templates found</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={matchingResults.matchedContracts.length === 0}
                            >
                                {`Send ${matchingResults.matchedContracts.length} Contract(s)`}
                            </Button>
                        </DialogFooter>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
